'use client';

import { useCallback, useMemo, useState } from 'react';

import { useTemporarySignup } from '@/features/marketing/hooks/use-temporary-signup';

import {
  CEFR_SKILLS,
  formatEuropassLine,
  overallCefrLevel,
  type CefrBand,
  type CefrSkill,
} from './cefr-logic';

import styles from './CefrQuiz.module.css';

const SUGGESTED_LANGUAGES = ['German', 'English', 'French', 'Spanish', 'Italian', 'Dutch'];

type Answers = Partial<Record<CefrSkill, CefrBand>>;

export function CefrQuiz() {
  const { createTemporaryUser } = useTemporarySignup();
  const [language, setLanguage] = useState('');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);

  const totalSteps = CEFR_SKILLS.length + 1;
  const skillIndex = step - 1;
  const currentSkill = skillIndex >= 0 ? CEFR_SKILLS[skillIndex] : null;
  const progressPct = ((step + (showResult ? 1 : 0)) / totalSteps) * 100;

  const skillResults = useMemo(
    () =>
      CEFR_SKILLS.map((skill) => ({
        skill: skill.label,
        level: answers[skill.id],
      })),
    [answers],
  );

  const overall = useMemo(() => {
    const levels = skillResults
      .map((item) => item.level)
      .filter((level): level is CefrBand => Boolean(level));
    return overallCefrLevel(levels);
  }, [skillResults]);

  const europassLine = formatEuropassLine(
    language,
    skillResults
      .filter((item): item is { skill: string; level: CefrBand } => Boolean(item.level))
      .map((item) => ({ skill: item.skill, level: item.level })),
    overall,
  );

  const selectBand = useCallback(
    (band: CefrBand) => {
      if (!currentSkill) return;
      setAnswers((prev) => ({ ...prev, [currentSkill.id]: band }));
      window.setTimeout(() => {
        if (skillIndex < CEFR_SKILLS.length - 1) {
          setStep((prev) => prev + 1);
        } else {
          setShowResult(true);
        }
      }, 280);
    },
    [currentSkill, skillIndex],
  );

  const startSkills = () => {
    if (language.trim().length < 2) return;
    setStep(1);
  };

  const reset = () => {
    setLanguage('');
    setStep(0);
    setAnswers({});
    setShowResult(false);
    setCopied(false);
  };

  const copyLine = async () => {
    try {
      await navigator.clipboard.writeText(europassLine);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  if (showResult) {
    return (
      <div className={styles.quiz}>
        <div className={styles.resultCard}>
          <p className={styles.levelEyebrow}>{language.trim()} — overall CEFR</p>
          <div className={styles.levelBadge} aria-label={`Overall level ${overall}`}>
            {overall}
          </div>
          <p className={styles.resultCopy}>
            Use the conservative overall level on your Europass language grid. Employers prefer
            honest CEFR grades over “fluent”.
          </p>

          <div className={styles.skillGrid}>
            {skillResults.map((item) =>
              item.level ? (
                <div key={item.skill} className={styles.skillChip}>
                  <span>{item.skill}</span>
                  <strong>{item.level}</strong>
                </div>
              ) : null,
            )}
          </div>

          <button type="button" className={styles.copyBtn} onClick={() => void copyLine()}>
            <span className="material-icons" aria-hidden="true">
              content_copy
            </span>
            {copied ? 'Copied for Europass' : 'Copy Europass line'}
          </button>

          <div className={styles.resultCtas}>
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={() => void createTemporaryUser('import')}
            >
              Add this to a Europass CV
            </button>
            <button type="button" className={styles.ctaSecondary} onClick={reset}>
              Test another language
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 0) {
    return (
      <div className={styles.quiz}>
        <div className={styles.progressHeader}>
          <span className={styles.stepLabel}>Step 1 of {totalSteps}</span>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
          </div>
        </div>
        <div className={styles.stepCard}>
          <div className={styles.stepIcon} aria-hidden="true">
            <span className="material-icons">translate</span>
          </div>
          <h2 className={styles.stepQuestion}>Which language are you assessing?</h2>
          <input
            className={styles.languageInput}
            type="text"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            placeholder="Type a language, e.g. German"
            aria-label="Language name"
          />
          <div className={styles.suggestions}>
            {SUGGESTED_LANGUAGES.map((name) => (
              <button
                key={name}
                type="button"
                className={styles.suggestion}
                onClick={() => setLanguage(name)}
              >
                {name}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.ctaPrimary}
            disabled={language.trim().length < 2}
            onClick={startSkills}
          >
            Start 4-skill test
          </button>
        </div>
      </div>
    );
  }

  if (!currentSkill) return null;

  return (
    <div className={styles.quiz}>
      <div className={styles.progressHeader}>
        <div className={styles.progressMeta}>
          <span className={styles.stepLabel}>
            {currentSkill.label} · Step {step + 1} of {totalSteps}
          </span>
          <span className={styles.languageTag}>{language.trim()}</span>
        </div>
        <div className={styles.progressTrack} role="progressbar" aria-valuenow={progressPct}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className={styles.stepCard} key={currentSkill.id}>
        <div className={styles.stepIcon} aria-hidden="true">
          <span className="material-icons">{currentSkill.icon}</span>
        </div>
        <h2 className={styles.stepQuestion}>{currentSkill.prompt}</h2>
        <div className={styles.options}>
          {currentSkill.options.map((option) => (
            <button
              key={option.band}
              type="button"
              className={`${styles.optionBtn} ${
                answers[currentSkill.id] === option.band ? styles.optionSelected : ''
              }`}
              onClick={() => selectBand(option.band)}
            >
              <span className={styles.bandPill}>{option.band}</span>
              <span className={styles.optionLabel}>{option.label}</span>
            </button>
          ))}
        </div>
        {step > 1 ? (
          <button type="button" className={styles.backBtn} onClick={() => setStep((prev) => prev - 1)}>
            <span className="material-icons" aria-hidden="true">
              arrow_back
            </span>
            Back
          </button>
        ) : null}
      </div>
    </div>
  );
}
